import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { pickMime, stopAllTracks } from "../../lib/interview/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECORD_SECONDS = 10;
const MAX_UPLOAD_ATTEMPTS = 4; // 1 initial + 3 retries
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function MobileEnvScan() {
  const { handoffToken } = useParams();
  const [phase, setPhase] = useState("loading"); // loading, ready, armed, recording, uploading, done, error
  const [errMsg, setErrMsg] = useState("");
  const [studentName, setStudentName] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(RECORD_SECONDS);
  const [uploadPct, setUploadPct] = useState(0);
  const [retryAttempt, setRetryAttempt] = useState(0); // 0 = first try, 1..3 = retry #
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoRef = useRef(null);
  const tickRef = useRef(null);
  const blobRef = useRef(null); // retained across retries so user doesn't re-record

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/env-scan/${handoffToken}`);
        if (data.status === "consumed") {
          setPhase("done");
          return;
        }
        setStudentName(data.student_name || "");
        setPhase("ready");
      } catch (e) {
        setPhase("error");
        setErrMsg("This link is invalid or has expired. Return to your laptop and generate a new QR code.");
      }
    })();
    return () => {
      clearInterval(tickRef.current);
      stopAllTracks(streamRef.current);
    };
    // eslint-disable-next-line
  }, []);

  const enableCam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 640, max: 854 },
          height: { ideal: 360, max: 480 },
          frameRate: { ideal: 15, max: 20 },
        },
        audio: false,
      });
      streamRef.current = s;
      setPhase("armed");
    } catch (e) {
      setPhase("error");
      setErrMsg("Camera permission denied. Please allow camera access and reload this page.");
    }
  };

  // Attach stream to <video> once mounted (armed / recording phases).
  useEffect(() => {
    if ((phase === "armed" || phase === "recording") && videoRef.current && streamRef.current) {
      try {
        if (videoRef.current.srcObject !== streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
        }
        const playPromise = videoRef.current.play();
        if (playPromise && typeof playPromise.catch === "function") playPromise.catch(() => {});
      } catch (e) { /* noop */ }
    }
  }, [phase]);

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = pickMime(true);
    const opts = {};
    if (mime) opts.mimeType = mime;
    opts.videoBitsPerSecond = 600_000;
    const mr = new MediaRecorder(streamRef.current, opts);
    mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mr.mimeType || "video/webm" });
      blobRef.current = blob;
      uploadWithRetry(blob);
    };
    mr.start();
    recorderRef.current = mr;
    setPhase("recording");
    setSecondsLeft(RECORD_SECONDS);
    let left = RECORD_SECONDS;
    tickRef.current = setInterval(() => {
      left -= 1;
      setSecondsLeft(left);
      if (left <= 0) {
        clearInterval(tickRef.current);
        try { mr.stop(); } catch (e) {}
      }
    }, 1000);
  };

  const uploadOnce = (blob) => {
    const fd = new FormData();
    fd.append("file", new File([blob], "env_scan.webm", { type: blob.type }));
    return axios.post(`${API}/public/env-scan/${handoffToken}/upload`, fd, {
      timeout: 120_000,
      onUploadProgress: (ev) => {
        if (ev && ev.total) {
          setUploadPct(Math.round((ev.loaded * 100) / ev.total));
        } else if (ev && ev.loaded) {
          const est = Math.min(99, Math.round((ev.loaded * 100) / Math.max(1, blob.size)));
          setUploadPct(est);
        }
      },
    });
  };

  const uploadWithRetry = async (blob) => {
    setPhase("uploading");
    setUploadPct(0);
    setRetryAttempt(0);
    // Stop camera tracks as soon as we move into uploading — saves battery and
    // frees the camera for other apps; we already have the blob in memory.
    stopAllTracks(streamRef.current);
    let lastErr = null;
    for (let attempt = 0; attempt < MAX_UPLOAD_ATTEMPTS; attempt++) {
      setRetryAttempt(attempt);
      setUploadPct(0);
      try {
        await uploadOnce(blob);
        setUploadPct(100);
        setPhase("done");
        return;
      } catch (e) {
        lastErr = e;
        // If the link is invalid/consumed, no point retrying.
        const status = e?.response?.status;
        if (status === 404 || status === 400) {
          setPhase("error");
          setErrMsg(e?.response?.data?.detail || "This link is no longer valid. Return to your laptop and generate a new QR code.");
          return;
        }
        // Exponential backoff: 1s, 2s, 4s before retries 1/2/3
        if (attempt < MAX_UPLOAD_ATTEMPTS - 1) {
          await sleep(1000 * Math.pow(2, attempt));
        }
      }
    }
    setPhase("upload_failed");
    setErrMsg(lastErr?.message || "Upload failed after several attempts.");
  };

  const retryUpload = () => {
    if (blobRef.current) uploadWithRetry(blobRef.current);
  };

  return (
    // h-[100svh] uses the *small* viewport height so the button never gets
    // hidden behind the mobile browser's URL bar or the Android nav bar.
    // pb-[env(safe-area-inset-bottom)] keeps CTAs above the home indicator.
    <div
      className="h-[100svh] max-h-[100svh] bg-[#0A2540] text-white flex flex-col overflow-hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="mobile-env-scan"
    >
      {/* Compact header — intentionally small so content fits one viewport. */}
      <div className="px-4 py-2 border-b border-white/10 flex-shrink-0">
        <div className="text-[9px] font-mono text-slate-300 tracking-[0.2em] uppercase">HIMT · 360° Room Sweep</div>
        <div className="text-sm font-bold mt-0.5 truncate">{studentName ? `Hello, ${studentName}` : "360° Environment Scan"}</div>
      </div>

      {phase === "loading" && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-sm text-slate-300">Loading…</div>
        </div>
      )}

      {phase === "error" && (
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div>
            <div className="text-red-400 text-3xl mb-3">✕</div>
            <div className="text-base" data-testid="env-scan-error-msg">{errMsg || "Something went wrong."}</div>
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div>
            <div className="text-emerald-400 text-5xl mb-3">✓</div>
            <div className="text-lg font-bold">Upload complete</div>
            <div className="text-sm text-slate-300 mt-2">Return to your laptop — the test will continue automatically.</div>
          </div>
        </div>
      )}

      {phase === "ready" && (
        <div className="flex-1 px-5 py-3 flex flex-col min-h-0">
          <h2 className="text-lg font-bold mb-2">Room sweep instructions</h2>
          <ol className="text-[13px] leading-snug text-slate-200 space-y-1.5 list-decimal pl-5">
            <li>Hold phone at arm's length, <strong>back camera</strong> facing your room.</li>
            <li>Tap <strong>Enable Camera</strong> and allow access.</li>
            <li>Tap <strong>Start 360° Scan</strong> — a 10-second timer begins.</li>
            <li><strong>Slowly rotate in a full circle</strong> — walls, ceiling, floor, behind chair.</li>
            <li>Video uploads automatically when the timer ends.</li>
          </ol>
          <div className="flex-1" />
          <button
            onClick={enableCam}
            className="w-full bg-white text-[#0A2540] font-bold py-3 rounded-sm tracking-wide active:bg-slate-200"
            data-testid="enable-phone-cam-btn"
          >
            Enable Camera
          </button>
        </div>
      )}

      {(phase === "armed" || phase === "recording") && (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="relative flex-1 bg-black min-h-0">
            <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 w-full h-full object-cover" />

            {/* Recording status ribbon — top, large, unmistakable */}
            {phase === "recording" && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white font-mono text-sm px-4 py-1.5 rounded-full shadow-lg animate-pulse tracking-wide">
                ● REC · {secondsLeft}s
              </div>
            )}

            {/* Armed state: status pill + overlayed start button near the bottom
                of the camera view (NOT at the screen's bottom edge — that area
                gets eaten by Android/Samsung nav bars on many devices). */}
            {phase === "armed" && (
              <>
                <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-black font-bold text-[11px] px-3 py-1 rounded-full shadow-lg tracking-wide">
                  CAMERA READY
                </div>
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                  <button
                    onClick={startRecording}
                    className="w-full bg-red-600 text-white font-bold py-4 text-base tracking-wide rounded-sm active:bg-red-700 shadow-2xl ring-2 ring-red-400/50"
                    data-testid="start-scan-btn"
                  >
                    ● Start 360° Scan (10 seconds)
                  </button>
                  <div className="text-center text-[11px] text-slate-200 mt-2">Hold phone steady · back camera facing room</div>
                </div>
              </>
            )}

            {phase === "recording" && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-4 text-center">
                <div className="text-sm font-bold">Rotate slowly in a full circle</div>
                <div className="text-xs text-slate-300 mt-1">Show every wall, ceiling, floor, behind the chair</div>
              </div>
            )}
          </div>
        </div>
      )}

      {phase === "uploading" && (
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div className="w-full max-w-xs">
            <div className="text-slate-200 text-lg">
              {retryAttempt === 0 ? "Uploading video…" : `Retrying upload (${retryAttempt}/${MAX_UPLOAD_ATTEMPTS - 1})…`}
            </div>
            <div className="mt-4 h-2 w-full bg-white/10 rounded-sm overflow-hidden" data-testid="env-scan-upload-progress">
              <div
                className="h-full bg-emerald-400 transition-all duration-300"
                style={{ width: `${uploadPct}%` }}
              />
            </div>
            <div className="mt-2 font-mono text-xs text-emerald-300">{uploadPct}%</div>
            <div className="text-xs text-slate-400 mt-3 font-mono">Do not close this page</div>
          </div>
        </div>
      )}

      {phase === "upload_failed" && (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="text-amber-400 text-4xl mb-3">⚠</div>
          <div className="text-base font-bold mb-1">Upload didn't go through</div>
          <div className="text-sm text-slate-300 mb-5 max-w-xs">
            We tried {MAX_UPLOAD_ATTEMPTS} times but the video couldn't reach our servers.
            Move closer to your Wi-Fi router or switch to mobile data, then tap Try Again.
          </div>
          <button
            onClick={retryUpload}
            className="bg-red-600 text-white font-bold py-3 px-8 rounded-sm tracking-wide active:bg-red-700"
            data-testid="retry-upload-btn"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
